import { Loader2Icon } from "lucide-react";
import { useState } from "react";
import type { ProjectCheckoutDeletionPreview } from "@cmmd-forge/contracts";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import {
  canConfirmProjectGroupRemoval,
  projectGroupRemovalResultLabel,
  projectGroupRemovalActionLabel,
} from "../projects/ProjectGroupRemovalDialog";
import {
  ProjectCheckoutDeletionControl,
  ProjectGroupRemovalTargetList,
} from "../projects/ProjectCheckoutDeletionControls";
import type { ProjectGroupRemovalTarget } from "~/projectGroupRemovalRunner";
import type { ProjectGroupRemovalResult } from "~/projectGroupRemovalRunner";
import type { RemovalStepLabel } from "./removalStepLabels";

export interface ProjectRemovalSummary {
  readonly checkoutCount: number;
  readonly threadCount: number;
  readonly checkouts: readonly {
    readonly cwd: string;
    readonly label: string;
    readonly environmentRole?: "local" | "cloud" | "remote";
  }[];
}

export interface SidebarRemoveProjectDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly projectName: string;
  readonly removesCloudRuntime: boolean;
  /**
   * Overrides the blast-radius sentence under the title.
   *
   * The default two-branch copy is written for a SIDEBAR row, where "no cloud
   * runtime" means a local project that merely leaves the active views. A
   * Forge Cloud registration with no Sprite yet is the same shape and a very
   * different fact — removing it is permanent — so that caller says so itself
   * instead of inheriting copy that understates what it does.
   */
  readonly description?: string;
  readonly isRemovingProject: boolean;
  /**
   * The step currently in flight, or `null` before the first one is reported.
   * Comes from `removeSidebarProject`'s progress reporter, so it is always one
   * of the labels emitted by the durable CMMD removal saga.
   */
  readonly removalStepLabel: RemovalStepLabel | null;
  readonly removalSummary: ProjectRemovalSummary;
  readonly fileRemoval?: {
    readonly targets: readonly ProjectGroupRemovalTarget[];
    readonly previews: ReadonlyMap<string, ProjectCheckoutDeletionPreview>;
    readonly results: readonly ProjectGroupRemovalResult[];
    readonly isPreviewing: boolean;
    readonly onDeleteFilesChange: (checked: boolean) => void;
  };
  readonly onConfirmRemove: (input: {
    readonly deleteFiles: boolean;
    readonly confirmation: string;
  }) => void;
}

/**
 * "Remove project" confirmation — extracted out of `SidebarProjectItem` so its
 * blast-radius copy (cloud-runtime warning, CMMD-delete checkbox, checkout/
 * thread summary) doesn't inflate the orchestrator's branching. Pure
 * presentational renderer; all decisions are made by the caller.
 *
 * IN-FLIGHT FEEDBACK (live defect C). CMMD persists a durable removal receipt,
 * then destroys and purges provider state asynchronously. That can take several
 * minutes under provider degradation and continues after this page closes. The
 * dialog names the persisted phase so waiting is legible; terminal errors carry
 * the receipt id for support and safe retry.
 */
/**
 * The in-flight region: which step is running, and that waiting is expected.
 *
 * Its own component so the dialog's own branching stays flat — the blast-radius
 * summary already made that body the most complex thing in this file.
 */
function RemoveProjectProgress(props: {
  readonly projectName: string;
  readonly stepLabel: RemovalStepLabel | null;
}) {
  return (
    <div
      data-slot="remove-project-progress"
      role="status"
      aria-live="polite"
      className="mt-3 flex items-start gap-2 rounded-md border border-border/60 bg-secondary/40 p-2.5 text-[11px] text-muted-foreground"
    >
      <Loader2Icon className="mt-px size-3 shrink-0 animate-spin" aria-hidden="true" />
      <span className="grid gap-0.5">
        {/* Falls back to a generic line rather than rendering nothing: there is
            a real gap between the click and the first reported step, and an
            empty progress area during it is the exact "nothing happened"
            impression this region exists to remove. */}
        <span className="text-foreground/80">
          {props.stepLabel ?? `Removing "${props.projectName}"`}…
        </span>
        <span className="text-muted-foreground/70">
          This may take several minutes. CMMD will continue cleanup if you close this page.
        </span>
      </span>
    </div>
  );
}

/** Checkouts and threads a removal takes with it. */
function RemoveProjectBlastRadius({
  removalSummary,
}: {
  readonly removalSummary: ProjectRemovalSummary;
}) {
  const threadSummaryLabel =
    removalSummary.threadCount === 0
      ? "No threads to archive."
      : `${removalSummary.threadCount} thread${
          removalSummary.threadCount === 1 ? "" : "s"
        } will be archived (recoverable).`;

  return (
    <div className="mt-3 rounded-md border border-border/60 bg-secondary/40 p-2.5 text-[11px] text-muted-foreground">
      {removalSummary.checkoutCount > 1 ? (
        <>
          <div className="font-medium text-foreground/80">
            Removes all {removalSummary.checkoutCount} checkouts of this project:
          </div>
          <ul className="mt-1 space-y-0.5">
            {removalSummary.checkouts.map((checkout) => (
              <li key={checkout.cwd} className="flex items-baseline gap-1.5">
                <span className="shrink-0 text-foreground/70">{checkout.label}</span>
                <span className="truncate text-muted-foreground/70" title={checkout.cwd}>
                  {checkout.cwd}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <div className={removalSummary.checkoutCount > 1 ? "mt-1.5" : ""}>{threadSummaryLabel}</div>
    </div>
  );
}

function SidebarProjectFileRemovalSection(props: {
  readonly fileRemoval: NonNullable<SidebarRemoveProjectDialogProps["fileRemoval"]>;
  readonly deleteFiles: boolean;
  readonly confirmation: string;
  readonly isRemovingProject: boolean;
  readonly onDeleteFilesChange: (checked: boolean) => void;
  readonly onConfirmationChange: (value: string) => void;
}) {
  return (
    <>
      <ProjectGroupRemovalTargetList
        targets={props.fileRemoval.targets}
        previews={props.fileRemoval.previews}
        expanded={props.deleteFiles}
      />
      <ProjectCheckoutDeletionControl
        checked={props.deleteFiles}
        confirmation={props.confirmation}
        disabled={props.isRemovingProject || props.fileRemoval.isPreviewing}
        previews={props.fileRemoval.previews}
        onCheckedChange={props.onDeleteFilesChange}
        onRetry={() => props.fileRemoval.onDeleteFilesChange(true)}
        onConfirmationChange={props.onConfirmationChange}
      />
      {props.fileRemoval.isPreviewing ? (
        <p className="text-xs text-muted-foreground">Checking checkout files...</p>
      ) : null}
      {props.fileRemoval.results.length > 0 ? (
        <ul className="grid gap-1 text-xs text-muted-foreground">
          {props.fileRemoval.results.map((result) => (
            <li key={`${result.target.environmentId}:${result.target.projectId}`}>
              {result.target.label}: {projectGroupRemovalResultLabel(result)}
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

function SidebarRemoveProjectDialogFooter(props: {
  readonly completed: boolean;
  readonly isRemovingProject: boolean;
  readonly isPreviewing: boolean;
  readonly canConfirm: boolean;
  readonly actionLabel: string;
  readonly onDone: () => void;
  readonly onConfirm: () => void;
}) {
  return (
    <AlertDialogFooter>
      {props.completed ? (
        <Button className="min-h-touch md:min-h-8" onClick={props.onDone}>
          Done
        </Button>
      ) : (
        <>
          <AlertDialogClose
            render={
              <Button
                className="min-h-touch md:min-h-8"
                variant="outline"
                disabled={props.isRemovingProject}
              />
            }
          >
            Cancel
          </AlertDialogClose>
          <Button
            className="min-h-touch md:min-h-8"
            variant="destructive"
            disabled={props.isRemovingProject || props.isPreviewing || !props.canConfirm}
            onClick={props.onConfirm}
          >
            {props.isRemovingProject ? "Removing..." : props.actionLabel}
          </Button>
        </>
      )}
    </AlertDialogFooter>
  );
}

export function SidebarRemoveProjectDialog(props: SidebarRemoveProjectDialogProps) {
  const { removalSummary } = props;
  const showBlastRadius =
    props.fileRemoval === undefined &&
    (removalSummary.checkoutCount > 1 || removalSummary.threadCount > 0);
  const [deleteFiles, setDeleteFiles] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const previews = props.fileRemoval?.previews ?? new Map();
  const targets = props.fileRemoval?.targets ?? [];
  const canConfirm = canConfirmProjectGroupRemoval({
    deleteFiles,
    targets,
    previews,
    confirmation,
  });
  const actionLabel = projectGroupRemovalActionLabel({
    mode: "project",
    deleteFiles,
    previews,
  });
  const completed =
    props.fileRemoval !== undefined &&
    props.fileRemoval.results.length > 0 &&
    props.fileRemoval.results.every((result) => result.metadata === "removed");

  return (
    <AlertDialog
      open={props.open}
      // Base UI's AlertDialog already blocks outside-press, but it still wires
      // the Escape key (`escapeKey: isTopmost`). Escape during a removal
      // therefore tore down the ONLY surface reporting that removal, leaving an
      // in-flight destructive operation running with nothing on screen. Swallow
      // close requests while it is in flight; the dialog closes itself the
      // moment the operation settles. Note Base UI passes (open, eventDetails),
      // so this must forward only what the caller's handler expects.
      onOpenChange={(open) => {
        if (!open && props.isRemovingProject) return;
        props.onOpenChange(open);
      }}
    >
      <AlertDialogPopup className="max-w-md">
        <AlertDialogHeader className="text-left">
          <AlertDialogTitle>Remove project?</AlertDialogTitle>
          <AlertDialogDescription>
            {/* One sentence for both. The cloud branch used to promise
                destroying "its cloud runtime", which was accurate only while a
                Sprite backed exactly one Project. An Environment hosts many
                now, so that click would end a machine the others are still
                running on. Ending the Environment is Settings, Connections. */}
            {props.description ? (
              <>{props.description} The connected CMMD project is kept.</>
            ) : (
              "Removes this Forge project and its threads. Files stay on disk. CMMD stays intact."
            )}
          </AlertDialogDescription>
          {showBlastRadius ? <RemoveProjectBlastRadius removalSummary={removalSummary} /> : null}
          {props.fileRemoval ? (
            <SidebarProjectFileRemovalSection
              fileRemoval={props.fileRemoval}
              deleteFiles={deleteFiles}
              confirmation={confirmation}
              isRemovingProject={completed || props.isRemovingProject}
              onDeleteFilesChange={(checked) => {
                setDeleteFiles(checked);
                if (!checked) setConfirmation("");
                props.fileRemoval?.onDeleteFilesChange(checked);
              }}
              onConfirmationChange={setConfirmation}
            />
          ) : null}
          {props.isRemovingProject ? (
            <RemoveProjectProgress
              projectName={props.projectName}
              stepLabel={props.removalStepLabel}
            />
          ) : null}
        </AlertDialogHeader>
        <SidebarRemoveProjectDialogFooter
          completed={completed}
          isRemovingProject={props.isRemovingProject}
          isPreviewing={props.fileRemoval?.isPreviewing === true}
          canConfirm={canConfirm}
          actionLabel={actionLabel}
          onDone={() => props.onOpenChange(false)}
          onConfirm={() => props.onConfirmRemove({ deleteFiles, confirmation })}
        />
      </AlertDialogPopup>
    </AlertDialog>
  );
}
